import css from './aio.less';
import type {TextareaHTMLAttributes} from "react";
import { useCallback} from "react"
import { useLayoutEffect, useRef} from "react"
import cn from 'classnames';
import {mergeAttrs} from "../../helpers/react-helpers";
import {useUuid} from "../../helpers/react-hooks";
import {useTestId} from '../../contexts/testid'

interface Props extends RequiredKeys<Pick<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange' | 'disabled' | 'onFocus' | 'onBlur' | 'defaultValue' | 'placeholder'>,'value'|'onChange'> {
    width?: number | string
    minWidth?: number | string
    maxWidth?: number | string
    label: string
    inputClass?: string
    labelClass?: string
    className?: string
    id?: string
    loading?: boolean
    testId?: string
}

export default function AioTextArea({id, width, minWidth, maxWidth, label, inputClass, labelClass, className, loading, value, testId, onChange, ...inputProps}: Props) {
    const inputId = useUuid(id);
    const ref = useRef<HTMLTextAreaElement|null>(null)
    testId = useTestId(testId)

    mergeAttrs(inputProps, {
        id: inputId,
        className: [css.textarea, inputClass],
    })

    useLayoutEffect(() => {
        if (ref.current) {
            autoSize(ref.current)
        }
    }, [value])

    const wrappedOnChange = useCallback((ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(ev)
        if (ref.current) {
            autoSize(ref.current)
        }
    },[onChange])

    return (
        <div className={cn(css.wrapper, {[css.disabled]: inputProps.disabled, [css.loading]: loading}, className)}
             style={{width, minWidth, maxWidth}}>
            <textarea ref={ref} value={value} data-testid={testId} onChange={wrappedOnChange} {...inputProps}/>
            <label htmlFor={inputId} className={cn(css.label, labelClass)}>{label}</label>
        </div>
    )
}

function autoSize(textarea: HTMLTextAreaElement) {
    textarea.style.height = '0';
    textarea.style.height = `${Math.max(textarea.scrollHeight + 3, 46)}px`;
}
